import type { Vec2 } from '../../geo/types';
import type { LifeClearance } from './routes';
import { routeClear } from './routes';
import { distance } from './streetGraph';

const RADIUS = 0.48;

/** Different preferred widths and travel-direction sides, tapered into fixed door approaches. */
export function pedestrianPath(
  points: readonly Vec2[],
  clearance: LifeClearance,
  preferred: number
): readonly Vec2[] {
  if (points.length < 3) return points;
  const samples: Vec2[] = [points[0]!];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!,
      b = points[i]!,
      d = distance(a, b);
    const n = Math.max(1, Math.ceil(d / 3));
    // The last sample is the point itself: interpolating to it can land one rounding step away.
    for (let j = 1; j <= n; j++)
      if (d > 0.01)
        samples.push(
          j === n ? b : [a[0] + ((b[0] - a[0]) * j) / n, a[1] + ((b[1] - a[1]) * j) / n]
        );
  }
  const cumulative = [0];
  for (let i = 1; i < samples.length; i++)
    cumulative.push(cumulative[i - 1]! + distance(samples[i - 1]!, samples[i]!));
  const total = cumulative.at(-1)!;
  const normals = samples.map((_, i): Vec2 => {
    const a = samples[Math.max(0, i - 1)]!,
      b = samples[Math.min(samples.length - 1, i + 1)]!;
    const length = Math.max(0.001, distance(a, b));
    return [(b[1] - a[1]) / length, -(b[0] - a[0]) / length];
  });
  const widths = cumulative.map((d) => preferred * Math.min(1, d / 5, (total - d) / 5));
  const at = (i: number): Vec2 => [
    samples[i]![0] + normals[i]![0] * widths[i]!,
    samples[i]![1] + normals[i]![1] * widths[i]!,
  ];
  // Restrict only the obstructed part, allowing the rest of a broad quay to remain broad.
  for (let pass = 0; pass < 12; pass++) {
    let changed = false;
    for (let i = 1; i < samples.length; i++) {
      if (routeClear({ a: at(i - 1), b: at(i), radius: RADIUS }, clearance)) continue;
      widths[i - 1] = pass > 7 ? 0 : widths[i - 1]! * 0.5;
      widths[i] = pass > 7 ? 0 : widths[i]! * 0.5;
      changed = true;
    }
    if (!changed) return samples.map((_, i) => at(i));
  }
  return points;
}
